import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useEffect, useMemo, useState } from 'react';

import {
  getUserProfileIsTest,
  getUserRoleIsOmbudsman,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { getIsSticked } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { defineIsActive } from 'KnmPackage/KnmHeaderModule/common/defineIsActive';
import {
  getDecisionActionsDropdownVisible,
  getDecisionData,
  getDecisionWatchOnlyByTestUser,
  getDecisionDataResigningRequired,
  getDecisionDataCreatedAt,
  getDecisionFormRef,
  getDecisionFormAgreementsValue,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import {
  decisionAddVisibleModal,
  decisionDeleteSign,
  decisionDownloadXml,
  decisionRestore,
  decisionRestoreToLatestVersion,
  decisionSetActionsDropdownVisible,
} from 'DecisionPackage/MainModule/actions/decisionActions';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';
import { IDecisionResultDecision } from 'APIPackage/DecisionModule/interfaces/IDecisionResultDecision';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';

import { useFormFieldWatch } from '../../../hooks/useFormFieldWatch';
import { IBooleanSelectOption } from '../../../KnmPackage/MainModule/interfaces/IBooleanSelectOption';

interface IDecisionHeaderDropdownProps {
  isOmbudsman: boolean | undefined;
  decisionData: IDecision | undefined;
  isProsecutorOrSuperProsecutor: boolean | undefined;
  isSupervisor: boolean | undefined;
  actionsDropdownVisible: boolean;
  location: 'header' | 'form';
  isHeaderSticked: boolean;
  handleDeleteDecision: () => void;
  isUserTest: boolean;
  onHandleSign: () => void;
  isWatchOnlyByTestUser: boolean;
  resigningRequired: boolean;
  settings: IAdminSetting[] | undefined;
  created: string | null | undefined;
  formRef: IDecisionFormRef | undefined;
  decisionFormAgreementsValue: IErknmFormAgreement[] | undefined;
}

const DecisionHeaderDropdownWithoutStore = ({
  handleDeleteDecision,
  isOmbudsman,
  decisionData,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  actionsDropdownVisible,
  location,
  isHeaderSticked,
  isUserTest,
  onHandleSign,
  isWatchOnlyByTestUser,
  resigningRequired,
  settings,
  created,
  formRef,
  decisionFormAgreementsValue,
}: IDecisionHeaderDropdownProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const isTypeCompleted = decisionData?.status?.code === KnmErknmStatusCodesEnum.TYPE_COMPLETED;
  const isTypeAppealed = decisionData?.status?.code === KnmErknmStatusCodesEnum.TYPE_APPEALED;

  const [isActive, setIsActive] = useState(defineIsActive(location, isHeaderSticked));

  useEffect(() => {
    setIsActive(defineIsActive(location, isHeaderSticked));
  }, [location, isHeaderSticked]);

  const handleDownloadXml = () => dispatch(decisionDownloadXml());
  const handleOpenSignUploadModal = () => dispatch(decisionAddVisibleModal(KnmModalsEnum.SIGN_UPLOAD_MODAL));
  const handleDeleteSign = () => dispatch(decisionDeleteSign());
  const handleRestoreDecision = () => dispatch(decisionRestore());
  const restoreToLatestSigned = () => {
    dispatch(decisionRestoreToLatestVersion());
  };

  const makeDropdownItemClickHandler = (onClick: (() => void) | undefined) => () => {
    dispatch(decisionSetActionsDropdownVisible(false));
    onClick?.();
  };

  function handleDropdownVisibleChange(visible: boolean) {
    if (isActive) {
      dispatch(decisionSetActionsDropdownVisible(visible));
    }
  }

  const handleOpenCancelPrescribeModal = () => dispatch(decisionAddVisibleModal(KnmModalsEnum.ERKNM_PRESCRIBE_CANCEL));

  const handleOpenCancelActModal = () => dispatch(decisionAddVisibleModal(KnmModalsEnum.ERKNM_ACT_CANCEL));

  const unsignedActionsItems: IDropdownItem[] = [
    {
      id: 'download-xml',
      content: <DropdownItem onClick={makeDropdownItemClickHandler(handleDownloadXml)}>Экспорт в XML</DropdownItem>,
    },
  ];

  const isNeedToAddAct = useFormFieldWatch(formRef?.current, 'isNeedToAddAct') as IBooleanSelectOption | undefined;

  const isDisabledSignButton = useMemo(() => {
    // Блокируем кнопку подписания, если
    // 1. В ПР выбрано поле "Будет ли добавлен акт - Да"
    // 2. АКТ - не добавлен / добавлен новый (но еще не сохранен)

    const isDirty = formRef?.current?.getState()?.dirty;

    return (
      isNeedToAddAct?.value && (!decisionData?.organization?.act || decisionData?.organization?.act?.isNew || isDirty)
    );
  }, [decisionData, isNeedToAddAct, formRef?.current]);

  if (!isUserTest) {
    unsignedActionsItems.push({
      id: 'upload-xml-signed',
      content: (
        <DropdownItem
          onClick={makeDropdownItemClickHandler(handleOpenSignUploadModal)}
          isDisabled={isDisabledSignButton}
        >
          Загрузить подпись
        </DropdownItem>
      ),
    });
  }

  const backupSignedActionItems: IDropdownItem[] = [
    {
      id: 'sign',
      content: (
        <DropdownItem id="signButton" onClick={makeDropdownItemClickHandler(onHandleSign)}>
          Подписать
        </DropdownItem>
      ),
    },
    {
      id: 'get-backup-signed-version',
      content: (
        <DropdownItem onClick={makeDropdownItemClickHandler(restoreToLatestSigned)}>
          Восстановить последний подписанный паспорт
        </DropdownItem>
      ),
    },
  ];

  const isDeleteSignDisabled = isProsecutorOrSuperProsecutor || !isSupervisor || isTypeCompleted;

  const signedActionsItems: IDropdownItem[] = [
    {
      id: 'divider-with-signature',
      isDivider: true,
    },
    {
      id: 'delete-sign',
      content: (
        <DropdownItem onClick={makeDropdownItemClickHandler(handleDeleteSign)} isDisabled={isDeleteSignDisabled}>
          Удалить подпись
        </DropdownItem>
      ),
    },
  ];

  const restoreItems: IDropdownItem[] = [
    {
      id: 'divider-restore',
      isDivider: true,
    },
    {
      id: 'restore',
      content: (
        <DropdownItem onClick={makeDropdownItemClickHandler(handleRestoreDecision)} isSuccess>
          Восстановить
        </DropdownItem>
      ),
    },
  ];
  const deleteItems: IDropdownItem[] = [
    {
      id: 'divider-delete',
      isDivider: true,
    },
    {
      id: 'delete',
      content: (
        <DropdownItem onClick={makeDropdownItemClickHandler(handleDeleteDecision)} isDanger>
          Удалить
        </DropdownItem>
      ),
    },
  ];

  const cancelPrescribe: IDropdownItem = {
    id: 'cancelPrescribe',
    content: <DropdownItem onClick={handleOpenCancelPrescribeModal}>Отменить предписание</DropdownItem>,
  };

  const cancelAct: IDropdownItem = {
    id: 'cancelAct',
    content: <DropdownItem onClick={handleOpenCancelActModal}>Отменить результаты</DropdownItem>,
  };

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const agreements =
    (formRef?.current.getFieldState('agreements' as keyof IDecisionForm)?.value as IErknmFormAgreement[]) ||
    decisionFormAgreementsValue;

  const canCancelPrescribe = useMemo(() => {
    const prescribes: IDecisionResultDecision[] = [];

    decisionData?.organization?.decisions?.forEach(decision => {
      if (
        (decision.kind?.code === KindResultDecisionCodesEnum.D_1 ||
          decision.kind?.code === KindResultDecisionCodesEnum.D_7) &&
        (decision.status === KnmResultDecisionStatusEnum.COMPLETED ||
          decision.status === KnmResultDecisionStatusEnum.NOT_COMPLETED) &&
        decision.signed
      ) {
        const bindedAgreement = agreements?.find(
          agreement =>
            agreement.prescription?.value === decision.guid &&
            (agreement.status === AgreementStatusEnum.DRAFT ||
              agreement.status === AgreementStatusEnum.READY_TO_APPROVE ||
              agreement.status === AgreementStatusEnum.ON_APPROVAL ||
              agreement.status === AgreementStatusEnum.APPROVED ||
              agreement.status === AgreementStatusEnum.PENDING_TERMINATION ||
              agreement.status === AgreementStatusEnum.COMPLETED),
        );

        if (!bindedAgreement) {
          prescribes.push(decision);
        }
      }
    });

    return isEnablePaperlessDocument && prescribes.length > 0;
  }, [isEnablePaperlessDocument, decisionData, agreements]);

  const canCancelAct = useMemo(() => {
    if (
      isEnablePaperlessDocument &&
      decisionData?.organization?.act?.signed &&
      !decisionData?.organization?.act.fileInvalidate
    ) {
      const prescribe = decisionData?.organization.decisions?.find(
        decision =>
          (decision.kind?.code === KindResultDecisionCodesEnum.D_1 ||
            decision.kind?.code === KindResultDecisionCodesEnum.D_7) &&
          (decision.status === KnmResultDecisionStatusEnum.COMPLETED ||
            decision.status === KnmResultDecisionStatusEnum.NOT_COMPLETED) &&
          decision.signed,
      );

      if (!prescribe) return true;

      const bindedAgreement = agreements?.find(
        agreement =>
          agreement.prescription?.value === prescribe?.guid &&
          (agreement.status === AgreementStatusEnum.DRAFT ||
            agreement.status === AgreementStatusEnum.READY_TO_APPROVE ||
            agreement.status === AgreementStatusEnum.ON_APPROVAL ||
            agreement.status === AgreementStatusEnum.APPROVED ||
            agreement.status === AgreementStatusEnum.PENDING_TERMINATION ||
            agreement.status === AgreementStatusEnum.COMPLETED),
      );

      return !bindedAgreement;
    }

    return false;
  }, [isEnablePaperlessDocument, decisionData]);

  const dropdownItems = useMemo(() => {
    let res: IDropdownItem[] = [];

    if (!decisionData?.id) {
      return res;
    }
    if (isProsecutorOrSuperProsecutor || isSupervisor) {
      if (decisionData.signed && !decisionData.resigningRequired) {
        if (!decisionData.deleted) {
          res = [...res, ...signedActionsItems.filter(item => (res.length === 0 ? !item.isDivider : true))];
        }
      } else if (!decisionData.deleted) {
        res = [...res, ...unsignedActionsItems];
      }
    }
    if (isSupervisor && resigningRequired && !isWatchOnlyByTestUser) {
      res = [...res, ...backupSignedActionItems];
    }
    if (decisionData?.deleted) {
      res = [...res, ...restoreItems];
    }
    if (!decisionData?.deleted && !isTypeCompleted && !isWatchOnlyByTestUser) {
      res = [...res, ...deleteItems];
    }
    if (isSupervisor && canCancelPrescribe) {
      res = [...res, cancelPrescribe];
    }
    if (isSupervisor && canCancelAct) {
      res = [...res, cancelAct];
    }

    return res;
  }, [isProsecutorOrSuperProsecutor, isSupervisor, decisionData, isDisabledSignButton, resigningRequired]);

  if (!decisionData?.status?.code || isOmbudsman || isTypeAppealed) {
    return null;
  }

  return (
    <Dropdown
      items={dropdownItems}
      visible={actionsDropdownVisible}
      disabled={Boolean(!decisionData?.id)}
      onVisibleChange={handleDropdownVisibleChange}
    />
  );
};

const mapStateToProps = (state: IRootState) => ({
  isOmbudsman: getUserRoleIsOmbudsman(state),
  decisionData: getDecisionData(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  actionsDropdownVisible: getDecisionActionsDropdownVisible(state),
  isHeaderSticked: getIsSticked(state),
  isUserTest: getUserProfileIsTest(state),
  isWatchOnlyByTestUser: getDecisionWatchOnlyByTestUser(state),
  resigningRequired: getDecisionDataResigningRequired(state),
  settings: getAdminFetchSettings(state),
  created: getDecisionDataCreatedAt(state),
  formRef: getDecisionFormRef(state),
  decisionFormAgreementsValue: getDecisionFormAgreementsValue(state),
});

export const DecisionHeaderDropdown = connect(mapStateToProps)(DecisionHeaderDropdownWithoutStore);
